/**
 * # EventManager
 *
 * ## Overview
 * A lightweight, Vue-compatible global event manager with jQuery-style namespace support.
 *
 * ## Features
 * - Register/Unregister events with optional namespaces.
 * - Trigger events safely (only when listeners exist).
 * - Supports Vue plugin installation (`Vue.use(EventManager)`).
 *
 * ## Example Usage
 * ```js
 * // Register event
 * EventManager.on('option:postpress:checked:fail.namespace', handler);
 *
 * // Remove events by namespace
 * EventManager.off('.namespace');
 *
 * // Trigger event
 * EventManager.trigger('option:postpress:checked:fail', { some: 'data' });
 * ```
 *
 * ---
 * @author SOULITY
 * @email  ho.kim@prione.co.kr
 * @version 1.0.0
 * @license Private
 */

class EventManager {
    constructor() {
        /**
         * Internal active events.
         * @type {Record<string, { timestamp: Date, handler: Function, namespace?: string }[]>}
         * @private
         */
        this.__activeEvents = {};
        /**
         * Internal trashed events (development mode only).
         * @type {Record<string, { timestamp: Date, event: string, namespace?: string }[]>}
         * @private
         */
        this.__trashedEvents = {};
        /**
         * Development mode flag.
         * @type {boolean}
         */
        this.__isDevelopment = false;
    }

    /**
     * Register an event handler.
     *
     * @param {string} eventName - The event name (optionally with namespace, e.g. "option:checked.fail").
     * @param {Function} handler - The callback to be executed when the event is triggered.
     *
     * @example
     * EventManager.on('option:checked.postpress', (e) => this.log(e.detail));
     */
    on(eventName, handler) {
        if (typeof eventName !== 'string' || typeof handler !== 'function') {
            this.log('[EventManager] Invalid .on() call:', eventName, handler);
            return;
        }

        const [base, namespace] = eventName.split('.');

        if (!this.__activeEvents[base]) {
            this.__activeEvents[base] = [];
        }

        this.__activeEvents[base].push({
            timestamp: new Date().toISOString(),
            handler,
            namespace,
        });

        window.addEventListener(base, handler);
    }

    /**
     * Register a one-time event handler. Auto-removes after first invocation.
     *
     * @param {string} eventName - The event name (optionally with namespace, e.g. "option:checked.ns").
     * @param {Function} handler - The callback to execute once.
     *
     * @example
     * EventManager.once('option:checked.postpress', (e) => this.log(e.detail));
     */
    once(eventName, handler) {
        if (typeof eventName !== 'string' || typeof handler !== 'function') {
            this.log('[EventManager] Invalid .once() call:', eventName, handler);
            return;
        }

        const [base, namespace] = eventName.split('.');
        const wrapper = (event) => {
            handler(event);

            // Remove only this wrapper from __activeEvents
            if (this.__activeEvents[base]) {
                this.__activeEvents[base] = this.__activeEvents[base].filter((entry) => entry.handler !== wrapper);
                if (this.__activeEvents[base].length === 0) {
                    delete this.__activeEvents[base];
                }
            }

            window.removeEventListener(base, wrapper);
            this.__addToTrash(base, namespace);
        };

        if (!this.__activeEvents[base]) {
            this.__activeEvents[base] = [];
        }

        this.__activeEvents[base].push({
            timestamp: new Date().toISOString(),
            handler: wrapper,
            namespace,
        });

        window.addEventListener(base, wrapper);
    }

    /**
     * Unregister an event handler or group of handlers.
     *
     * @param {string} [eventName=''] - The event name or namespace.
     * Supports:
     * - `.namespace` → remove all handlers within that namespace.
     * - `event.namespace` → remove one event with that namespace.
     * - `event` → remove all handlers for that event.
     *
     * @example
     * EventManager.off('.postpress');
     * EventManager.off('option:checked');
     * EventManager.off('option:checked.postpress');
     */
    off(eventName = '') {
        // Namespace only: .namespace
        if (eventName.startsWith('.')) {
            const ns = eventName.slice(1);
            let removed = false;

            Object.entries(this.__activeEvents).forEach(([base, handlers]) => {
                if (!this.has(base)) return;

                const remaining = handlers.filter(({ handler, namespace }) => {
                    if (namespace === ns) {
                        window.removeEventListener(base, handler);
                        removed = true;
                        this.__addToTrash(base, namespace);
                        return false;
                    }
                    return true;
                });

                this.__activeEvents[base] = remaining;
            });

            if (!removed) {
                this.log(`[EventManager] No listeners found for namespace '.${ns}'`);
            }
            return;
        }

        const [base, ns] = eventName.split('.');

        if (!this.has(base)) {
            this.log(`[EventManager] No listeners found for '${eventName}'`);
            return;
        }

        const handlers = this.__activeEvents[base] || [];

        // Remove all for this base
        if (!ns) {
            handlers.forEach(({ handler, namespace }) => {
                window.removeEventListener(base, handler);
                this.__addToTrash(base, namespace);
            });
            delete this.__activeEvents[base];
            return;
        }

        this.__activeEvents[base] = handlers.filter(({ handler, namespace }) => {
            if (namespace === ns) {
                window.removeEventListener(base, handler);
                this.__addToTrash(base, namespace);
                return false;
            }
            return true;
        });

        // If nothing left, cleanup the base
        if (this.__activeEvents[base].length === 0) {
            delete this.__activeEvents[base];
        }
    }

    /**
     * Check whether an event has registered handlers.
     *
     * @param {string} eventName - The event name (base, no namespace).
     * @returns {boolean} True if at least one listener exists.
     *
     * @example
     * if (EventManager.has('option:checked')) {
     *     this.log('There are listeners for this event');
     * }
     */
    has(eventName) {
        if (typeof eventName !== 'string' || !eventName.trim()) return false;
        const handlers = this.__activeEvents[eventName];
        return !!(handlers && handlers.length > 0);
    }

    /**
     * Trigger a custom event.
     * Will only dispatch if at least one listener is registered.
     *
     * @param {string} eventName - The event name (base, no namespace).
     * @param {object} [detail={}] - Optional event data (accessible via `event.detail`).
     *
     * @example
     * EventManager.trigger('option:checked', { status: 'fail' });
     */
    trigger(eventName, detail = {}) {
        if (!this.has(eventName)) {
            this.log(`[EventManager] No listeners for '${eventName}', skipped.`);
            return;
        }

        try {
            window.dispatchEvent(new CustomEvent(eventName, { detail }));
        } catch (e) {
            console.error(`[EventManager] Error triggering event '${eventName}':`, e);
        }
    }

    /**
     * Trigger async event and wait for all listeners to resolve.
     *
     * @param {string} eventName - Base event name (no namespace).
     * @param {object} [detail={}] - Custom detail payload.
     * @param {object} [options={}]
     * @param {boolean} [options.suppressErrors=true] - Continue even if some handlers fail.
     *
     * @returns {Promise<{ success: boolean, results: { success: boolean, error?: Error }[] }>}
     *
     * @example
     * await EventManager.triggerAsync('option:checked', { id: 10 });
     */
    async triggerAsync(eventName, detail = {}, options = {}) {
        const { suppressErrors = true } = options;

        if (!this.has(eventName)) {
            this.log(`[EventManager] No listeners for '${eventName}', skipped.`);
            return { success: true, results: [] };
        }

        const handlers = this.__activeEvents[eventName] || [];
        const event = new CustomEvent(eventName, { detail });

        // 각 핸들러 실행 + 에러 수집
        const settled = await Promise.allSettled(
            handlers.map(({ handler, namespace }) =>
                Promise.resolve()
                    .then(() => handler(event))
                    .catch((err) => {
                        const nsInfo = namespace ? `.${namespace}` : '';
                        this.log(`[EventManager] Handler error in '${eventName}${nsInfo}':`, err);
                        throw err;
                    }),
            ),
        );

        // settled 결과 분석
        const results = settled.map((result) => ({
            success: result.status === 'fulfilled',
            error: result.status === 'rejected' ? result.reason : undefined,
        }));

        const allSuccess = results.every((r) => r.success);

        // suppressErrors = false 이면 AggregateError 던짐
        if (!suppressErrors && !allSuccess) {
            const errors = results.filter((r) => !r.success).map((r) => r.error);
            throw new AggregateError(errors, `Some handlers failed for event '${eventName}'`);
        }

        return { success: allSuccess, results };
    }

    /**
     * A flexible unique namespace with random string.
     *
     * ## Options
     * - `upper` (boolean) : Allow uppercase characters (default: `false`)
     *
     * ## Examples
     * ```js
     * // Default
     * EventManager.unamespace();
     * // → "a9dkq2"
     *
     * // Custom length
     * EventManager.unamespace(8);
     * // → "k29ad0qp"
     *
     * // Namespace prefix + 6 chars
     * EventManager.unamespace("user");
     * // → "user-a9kd92"
     *
     * // Namespace + length
     * EventManager.unamespace("user", 8);
     * // → "user-k2a9d0qp"
     *
     * // Length + uppercase
     * EventManager.unamespace(8, true);
     * // → "AbDK8Z2L"
     *
     * // Namespace + uppercase
     * EventManager.unamespace("app", true);
     * // → "app-AkD92z"
     *
     * // Namespace + length + uppercase
     * EventManager.unamespace("api", 8, true);
     * // → "api-AkD029Pi"
     * ```
     *
     * @param {string|number}  [arg1] - Namespace or length
     * @param {number|boolean} [arg2] - Length or uppercase flag
     * @param {boolean}        [arg3] - Uppercase flag
     * @returns {string}
     * @memberof EventManager
     */
    unamespace() {
        let namespace = '';
        let length = 6; // 기본 길이
        let upper = false; // 기본 옵션

        const arg1 = arguments[0];
        const arg2 = arguments[1];
        const arg3 = arguments[2];

        // ()
        if (arguments.length === 0) {
            // length = 6
            // upper = false
        }
        // (length)
        else if (arguments.length === 1 && typeof arg1 === 'number') {
            length = arg1;
        }
        // (namespace)
        else if (arguments.length === 1 && typeof arg1 === 'string') {
            namespace = arg1;
        }
        // (namespace, length)
        else if (arguments.length === 2 && typeof arg1 === 'string' && typeof arg2 === 'number') {
            namespace = arg1;
            length = arg2;
        }
        // (length, upper)
        else if (arguments.length === 2 && typeof arg1 === 'number' && typeof arg2 === 'boolean') {
            length = arg1;
            upper = arg2;
        }
        // (namespace, upper)
        else if (arguments.length === 2 && typeof arg1 === 'string' && typeof arg2 === 'boolean') {
            namespace = arg1;
            upper = arg2;
        }
        // (namespace, length, upper)
        else if (arguments.length === 3 && typeof arg1 === 'string' && typeof arg2 === 'number' && typeof arg3 === 'boolean') {
            namespace = arg1;
            length = arg2;
            upper = arg3;
        } else {
            throw new Error('Invalid arguments');
        }

        // 문자셋 생성
        let chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
        if (upper) chars += 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';

        const r = Array.from({ length }, () => chars[Math.floor(Math.random() * chars.length)]).join('');

        return namespace ? `${namespace}-${r}` : r;
    }

    /**
     * Display the current registry of events in a readable table format (for debugging).
     *
     * @example
     * EventManager.debug();
     */
    debug() {
        this.log('[EventManager] Active Events:');
        console.table(
            Object.entries(this.__activeEvents).flatMap(([base, list]) =>
                list.map((item) => ({
                    event: base,
                    namespace: item.namespace || '(none)',
                    timestamp: item.timestamp,
                })),
            ),
        );

        this.log('[EventManager] Trashed Events:');
        console.table(
            Object.entries(this.__trashedEvents).flatMap(([key, list]) =>
                list.map((item) => ({
                    event: item.event,
                    namespace: item.namespace || '(none)',
                    timestamp: item.timestamp,
                })),
            ),
        );
    }

    /**
     * Logs messages to the console
     *
     * @param {...any} args - The arguments to log.
     * @example
     * EventManager.log('This will only show in development mode');
     */
    log(...args) {
        if (this.__isDevelopment) {
            // console.log('[EventManager]', ...args);
        }
    }

    /**
     * Add removed event to trash registry (development mode only).
     *
     * @param {string} base - The base event name.
     * @param {string|undefined} namespace - The namespace (optional).
     * @private
     */
    __addToTrash(base, namespace) {
        if (!this.__isDevelopment) return;

        const key = namespace ? `${base}.${namespace}` : base;
        if (!this.__trashedEvents[key]) this.__trashedEvents[key] = [];

        this.__trashedEvents[key].push({
            timestamp: new Date().toISOString(),
            event: base,
            namespace: namespace,
        });
    }

    /**
     * Vue plugin installation.
     * Adds `$em` instance property to all Vue components.
     *
     * @param {any} Vue - The Vue constructor.
     * @example
     * Vue.use(EventManager);
     */
    install(Vue) {
        this.__isDevelopment = process?.env?.NODE_ENV === 'development';

        // Vue 3
        if (Vue.config?.globalProperties) {
            Vue.config.globalProperties.$em = this;
            Vue.provide('eventManager', this);
        }
        // Vue 2
        else if (Vue.prototype) {
            Vue.prototype.$em = this;
        } else {
            console.error('[EventManager] Unsupported Vue version');
        }
    }
}

// Export as singleton instance
const instance = new EventManager();
export default instance;
